<script setup lang="ts">
/**
 * /orders/:orderNo —— 订单只读详情:概览八行 + 四节点留证卡(v2 · G3;S-48 按画板重排)。
 *
 * ═══ 凭证是**真的证据**,不是设计稿里的演示照片 ═══
 *
 * 四个节点(取货 / 起飞 / 送达 / 空机返回)的照片是监管的兜底证据(M-04),
 * 页面只渲染这一单**真实登记过**的 proofs(短时效签名 URL,私有读 Y-02)。
 * 没留证的节点照画板画一个空框 + 「待留证」—— 空就是空,
 * **绝不拿设计稿里那两张演示照片充当凭证**:那是证据,假不得。
 * 打桩存储(STORAGE_MODE=stub)下签名 URL 打不开,画「照片暂不可用」;
 * 那与「链接已过期」是两句不同的话(见下面 markBroken 的注释)。
 *
 * ═══ 概览八行逐行都有出处(缺口照实登记)═══
 *
 *   状态     = status(中文走 @sea-drone/ui 的 statusLabel 单点)
 *   路线     = originArea → destinationArea(区域;空画「未标注」)——
 *             **画板画的是锚地/起降场点名,而 gov 返回体里只有区域**(见下面那条登记)
 *   距离     = **契约里没有 distanceKm** → 「—」(见下面那条登记)
 *   承运商   = carrierName;承接飞手 = pilotName(空由 FieldGrid 画「—」)
 *   下单时间 = createdAt(东八区、只到分钟;Y-03)
 *   起飞时间 = **起飞那张凭证的时刻**(takenAt 优先、退 uploadedAt;先例 AS-080③ ——
 *             与飞手作业页 M15 的「起飞时间」取同一个数)。GovOrderDetail 里没有
 *             任何时刻字段能答这一行,而凭证区恰好把它答了;取不到画「—」
 *   凭证进度 = 已留证的节点数 / 4 —— 分母走共享包的 PROOF_NODE,不写死 4
 *
 * ═══ 视频区位:画板把它压成了一行注 ═══
 *
 * 视频直播、三天回放、断流告警、起降场画面本期整块不做(L0-06 搁置、M-09 排除),
 * 旧版这里是一整块面板(多段长文案)。画板 G3 把它压成凭证卡底一行
 * 「本期没有视频,这是事先讲好的空。」—— 按画板落;那行注背后的依据
 * (哪条决策、为什么)照旧留在凭证卡的口径说明里,演示时答得上来。
 * 返回体里的 videoPlaceholder 恒为 false(契约里是 enum: [false]),
 * 它的用处就是让这件事在页面上看得见 —— 页面不拿它做判断。
 *
 * ═══ 节点名与时刻 ═══
 *
 * 节点名走 proofNodeLabel(单点定义在 shared 的 PROOF_NODE_LABEL):
 * 画板写「空机返回」,单点表里是「无人机空机返回」—— 同一枚节点在另三个端与
 * 服务端错误话术里都是那个长名字,以单点为准。四张卡按 PROOF_NODE 的履约次序摆,
 * 不依赖接口返回顺序;未留证的节点**不出现在返回体里**,按单点清单补全。
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useMessage } from 'naive-ui'
import { formatShanghaiMinute, PROOF_NODE } from '@sea-drone/shared'
import {
  ErrorBanner,
  FieldGrid,
  NavIcon,
  PanelCard,
  proofNodeLabel,
  statusLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import { api, ApiError, type GovOrderDetail, type Proof } from '../api/client'

const props = defineProps<{ orderNo: string }>()
const message = useMessage()
const router = useRouter()

const order = ref<GovOrderDetail | null>(null)
const lastError = ref<string | null>(null)
/** 已经加载失败的缩略图(按 fileKey 记)。@error 只对 img 触发一次,不记下来就会一直重试破图 */
const brokenPhotos = ref<Set<string>>(new Set())

const CARD_NOTES: readonly ScopeNote[] = [
  {
    title: '四个节点为什么必须留证',
    body: '取货、起飞、送达、空机返回四处必须留证才能推进状态(M-04)—— 这四个节点是监管的兜底证据。照片是私有读,下面每个链接都是现签的短时效地址(Y-02),过期后刷新本页会重新签发。',
    refs: ['M-04', 'Y-02'],
  },
  {
    title: '本期没有视频(卡底那行注的依据)',
    body: '视频直播、三天回放、断流告警、起降场画面整块不在本期交付范围(L0-06 搁置、M-09 排除),平台侧也不预选流媒体方案。等推流箱体方案确定(BLK-02 解除)后重开 L0-06,届时这里才是画面 —— 现在这是显式的空,不是加载失败。',
    refs: ['L0-06', 'M-09'],
  },
]

// ── 两行没有字段可读(画板画了、gov 返回体答不出)─────────────
//
// @unimplemented(reason="画板概览的「距离」行在契约里没有对应字段 —— GovOrderDetail 只有区域、承运商、飞手与凭证,没有 distanceKm。拿区域间的直线距离现算是一个编出来的数(它既不是计价用的距离,也与飞手端那个数不同源)。按「拿不到就画破折号」处理。要它先给契约加字段(小程序飞手端有一张同名口径,但那是 PilotOrderDetail,不是同一张表)", owner="me", blocks="G3 概览「距离」行")
//
// @unimplemented(reason="画板概览的「路线」行画的是锚地/起降场点名(「普陀山锚地 → 定海起降场」),而 gov 返回体里只有起点/终点**区域**(originArea / destinationArea)。这一行按区域渲染(空画「未标注」),点名要它先给契约加字段 —— 从区域名猜一个锚地是一句看起来完全正常的假话", owner="me", blocks="G3 概览「路线」行的点名")

/**
 * 按四个节点的**履约次序**排(单点定义在 shared 的 PROOF_NODE,与状态推进表一一对应)。
 * 未留证的节点不在返回体里 —— 下面按 PROOF_NODE 补「待留证」,不把「没有这一行」
 * 读成「没有这个节点」。拍摄时刻与登记时刻走同一处派生(见 proofSubText)。
 */
const byNode = computed(() => new Map((order.value?.proofs ?? []).map((p) => [p.node, p])))

/** 已留证的节点数(「N / 4 已留证」的 N;分母是共享包的节点清单) */
const provedCount = computed(() => (order.value?.proofs ?? []).length)

/** 一切「N / 4 已留证」的写法只有这一处(与飞手作业页 M15 同一形态) */
const proofProgressText = computed(() => `${provedCount.value} / ${PROOF_NODE.length} 已留证`)

/**
 * 起飞时间 = 起飞那张凭证的时刻(takenAt 优先、退到 uploadedAt,东八区只到分钟)。
 * 先例 AS-080③:与飞手端 M15 的「起飞时间」取的是同一个数 —— 同一单在两个端
 * 显示两个不同的起飞时刻,读起来两边都很正常。取不到画「—」。
 */
const takeoffText = computed(() => {
  const p = byNode.value.get('took_off')
  if (p === undefined) return null
  return formatShanghaiMinute(p.takenAt ?? p.uploadedAt)
})

/** 概览八行(画板 G3 的行序与行名逐字)。空值交给 FieldGrid 画「—」 */
const overviewFields = computed(() => {
  const o = order.value
  if (o === null) return []
  return [
    { label: '状态', value: statusLabel(o.status) },
    { label: '路线', value: `${o.originArea ?? '未标注'} → ${o.destinationArea ?? '未标注'}` },
    // 距离:契约里没有这个字段 —— 画「—」,理由见上面那条 @unimplemented
    { label: '距离', value: null },
    { label: '承运商', value: o.carrierName },
    { label: '承接飞手', value: o.pilotName },
    { label: '下单时间', value: formatShanghaiMinute(o.createdAt) },
    { label: '起飞时间', value: takeoffText.value },
    { label: '凭证进度', value: proofProgressText.value },
  ]
})

/** 一张卡的副行:已留证给时刻(takenAt 优先、退 uploadedAt),未留证写「待留证」 */
function proofSubText(p: Proof | undefined): string {
  if (p === undefined) return '待留证'
  return formatShanghaiMinute(p.takenAt ?? p.uploadedAt)
}

/** 四张节点卡(按履约次序;节点名走 proofNodeLabel 单点) */
const proofRows = computed(() =>
  PROOF_NODE.map((node) => {
    const p = byNode.value.get(node)
    return { node, label: proofNodeLabel(node), proved: p !== undefined, sub: proofSubText(p), proof: p ?? null }
  }),
)

/** 链接过期用既有的判断(urlExpiresAt 早于此刻)—— 与运营端订单详情同一条 */
function expired(p: Proof): boolean {
  return new Date(p.urlExpiresAt).getTime() < Date.now()
}

/**
 * 图片加载失败与「签名过期」是**两件事**,不能合并成一句话。
 *
 * 本地 STORAGE_MODE=stub 时桶域名根本不存在,照片是**永远**加载不出来的;
 * 网断掉、桶挂了、对象被删了也是同一个现象。把它们一律说成「链接已过期,刷新重新签发」,
 * 演示现场那句话就是假的 —— 刷新一百次还是打不开,而且它会让人以为存储坏了。
 * 所以这一支只说事实:照片暂不可用。原因不猜。
 */
function markBroken(fileKey: string): void {
  brokenPhotos.value.add(fileKey)
}

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function load(): Promise<void> {
  try {
    order.value = await api.getOrder(props.orderNo)
    brokenPhotos.value = new Set()
    lastError.value = null
  } catch (e) {
    order.value = null
    show(e)
  }
}

/** 单号不存在或接口失败时回列表 —— 404 的那句话由错误条原样显示 */
function goBack(): void {
  void router.push('/orders')
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page">
    <!-- 页头:返回 + 单号(画板只有这两项,没有右侧动作、没有状态标签) -->
    <div class="topbar">
      <button type="button" class="back" title="返回订单列表" @click="goBack">
        <NavIcon name="back" :size="18" />
      </button>
      <h1 class="order-no">{{ props.orderNo }}</h1>
    </div>

    <ErrorBanner tone="dark" title="加载失败" :message="lastError" />

    <div v-if="order" class="detail">
      <!-- 左:概览卡(八行,名左值右) -->
      <PanelCard tone="dark" title="订单概览" glow>
        <FieldGrid tone="dark" inline :columns="1" :items="overviewFields" />
      </PanelCard>

      <!--
        右:四节点留证卡。四张卡按履约次序;已留证给绿点 + 时刻 + 真实照片
        (现签 URL,点开新标签页看原图),未留证给灰点 + 「待留证」+ 空框。
        空位就是空 —— 不拿设计稿里的演示照片充当凭证(那是证据,假不得)。
      -->
      <PanelCard tone="dark" title="飞行节点凭证" :notes="CARD_NOTES" glow>
        <template #extra>
          <span class="proof-head-note">拍摄即留证,不可补拍或替换</span>
        </template>

        <ol class="proofs">
          <li v-for="row in proofRows" :key="row.node" class="proof-card">
            <span class="proof-dot" :class="{ done: row.proved }" aria-hidden="true" />
            <span class="proof-text">
              <span class="proof-node" :class="{ done: row.proved }">{{ row.label }}</span>
              <span class="proof-sub">{{ row.sub }}</span>
            </span>

            <!-- 三种状态互斥,而且是**替换**不是叠加:img 一旦失败就从树里摘掉,不留破图 -->
            <template v-if="row.proof && !expired(row.proof) && !brokenPhotos.has(row.proof.fileKey)">
              <a class="proof-thumb" :href="row.proof.url" target="_blank" rel="noreferrer" title="打开照片">
                <img
                  class="proof-img"
                  :src="row.proof.url"
                  alt="节点凭证照片"
                  loading="lazy"
                  @error="markBroken(row.proof.fileKey)"
                />
              </a>
            </template>
            <div v-else-if="row.proof" class="proof-slot">
              <span class="proof-lost">
                {{ expired(row.proof) ? '链接已过期，刷新本页重新签发' : '照片暂不可用' }}
              </span>
            </div>
            <div v-else class="proof-empty" aria-hidden="true">—</div>
          </li>
        </ol>

        <!-- 卡底一行注(画板逐字):视频整块不做,这件事在演示之前就说得清 -->
        <p class="video-note">本期没有视频,这是事先讲好的空。</p>
      </PanelCard>
    </div>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── 页头 ───────────────────────────────────────────── */

.topbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  cursor: pointer;
}

.back:hover {
  color: var(--sd-text1);
  background: var(--sd-surface-alt);
}

.order-no {
  margin: 0;
  font-family: var(--sd-font-mono);
  font-size: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
}

/* ── 双列(左概览 460:右凭证整条高)──────────────────── */

.detail {
  display: grid;
  grid-template-columns: minmax(0, 460px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

/* ── 凭证卡 ─────────────────────────────────────────── */

.proof-head-note {
  font-size: 12px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.proofs {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 每张节点卡:10×10 圆点 + 节点名/时刻 + 96×64 照片格(画板的三段横卡) */
.proof-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
}

.proof-dot {
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: var(--sd-radius-pill);
  background: var(--sd-text3);
}

.proof-dot.done {
  background: var(--sd-success);
}

.proof-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.proof-node {
  font-size: 14px;
  color: var(--sd-text3);
}

.proof-node.done {
  color: var(--sd-text1);
}

.proof-sub {
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}

/* 三种状态占同一个尺寸的框:照片来没来,版面都不跳 */
.proof-thumb,
.proof-slot,
.proof-empty {
  box-sizing: border-box;
  width: 96px;
  height: 64px;
  flex: none;
  border-radius: var(--sd-radius-sm);
}

.proof-thumb {
  display: block;
}

.proof-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--sd-radius-sm);
}

.proof-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  text-align: center;
  background: var(--sd-bg-deep);
  border: 1px solid var(--sd-border);
}

.proof-lost {
  font-size: 11px;
  line-height: 1.5;
  color: var(--sd-text3);
}

/* 未留证的空框:深底 + 描边 + 居中一枚「—」(画板形态) */
.proof-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--sd-text3);
  background: var(--sd-bg-deep);
  border: 1px solid var(--sd-border);
}

/* 卡底那行注:视频整块不做的显式说明(画板逐字) */
.video-note {
  margin: 16px 0 0;
  font-size: 12px;
  color: var(--sd-text3);
}

@media (max-width: 1200px) {
  .detail {
    grid-template-columns: 1fr;
  }
}
</style>
